import { useEffect, useRef, type ReactNode } from "react"
import { Box, Card, HStack, Icon, Text, VStack } from "@chakra-ui/react"
import { FaLock, FaLockOpen, FaPenToSquare, FaTrash } from "react-icons/fa6"
import { Switch } from "../../chakra/Switch"
import { Tooltip } from "../../chakra/Tooltip"
import { StatusTag } from "../Chip"
import { TitleWithChips } from "../TitleWithChips"
import { MUTED } from "./alertSummary"

// Every card sits slightly raised. The theme's card keyframes repeat this shadow, since an
// animated box-shadow replaces it while it runs
const CARD_SHADOW = "0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06)"
// A card being edited: a Logiq blue border with a slow pulse round it, back to plain once
// saved or cancelled; the pulse is held still for people who prefer less motion
const EDITING_BORDER = "#39aed2"
const EDITING_PULSE = {
  animation: "alertCardEditing 2.4s ease-in-out infinite",
  "@media (prefers-reduced-motion: reduce)": {
    animation: "none",
    boxShadow: `0 0 0 2px rgba(57, 174, 210, 0.3), ${CARD_SHADOW}`,
  },
}
/** The line between an edited card's summary, its edit panel and the panel's buttons. */
export const EDITING_DIVIDER = "#CDEAF3"
// A blue glow that fades, so the eye finds a card after its editor closes; held still
// for people who prefer less motion. Keyframes in the shared theme.
const HIGHLIGHT = {
  animation: "alertCardHighlight 1.6s ease-out",
  "@media (prefers-reduced-motion: reduce)": { animation: "none" },
}

export interface AlertSettingsCardControls {
  onToggle: (enabled: boolean) => void
  toggling?: boolean
  /** e.g. a new alert, which can't be switched on until it's created */
  toggleDisabled?: boolean
  /** Absent: the alert can't be edited (e.g. an event-based Standard Alert) */
  onEdit?: () => void
  /** Absent: the alert can't be deleted (every Standard Alert) */
  onDelete?: () => void
}

export interface AlertSettingsCardProps {
  /** The alert's name, for accessible labels */
  label: string
  /** The name, or its input while editing */
  title: ReactNode
  chips?: ReactNode
  /** What the alert fires on, in one line */
  summary?: ReactNode
  description?: string | null
  isEnabled: boolean
  editing?: boolean
  /** Omit for a read-only card: its status only, with no switch, lock, edit or delete */
  controls?: AlertSettingsCardControls
  /** The edit panel, shown while editing */
  children?: ReactNode
  /** Just out of edit state: scrolled back into view and briefly outlined */
  highlighted?: boolean
}

/**
 * One alert on an alert configuration page, in the Systems card's layout: name, chips and
 * conditions on the left; the Active/Inactive tag beside the switch at the top right, and
 * Edit, Delete and the lock just under them. Switching an alert on locks it:
 * edit and delete wait until it's off.
 */
export function AlertSettingsCard({
  label,
  title,
  chips,
  summary,
  description,
  isEnabled,
  editing = false,
  controls,
  children,
  highlighted = false,
}: AlertSettingsCardProps) {
  const ref = useRef<HTMLDivElement>(null)
  // A card shrinks as its editor closes, which can leave it out of view
  useEffect(() => {
    if (highlighted) ref.current?.scrollIntoView?.({ block: "nearest", behavior: "smooth" })
  }, [highlighted])

  return (
    <Card.Root
      ref={ref}
      variant="outline"
      data-highlighted={highlighted || undefined}
      css={highlighted ? HIGHLIGHT : editing ? EDITING_PULSE : undefined}
      aria-label={label}
      role="group"
      data-editing={editing || undefined}
      bg="white"
      boxShadow={CARD_SHADOW}
      borderColor={editing ? EDITING_BORDER : undefined}
      transition="border-color 0.2s ease"
    >
      <Card.Body p={4}>
        <HStack justify="space-between" align="start" gap={6}>
          <VStack align="stretch" gap={2} minW={0} flex={1}>
            {/* The chips beside the name, lined up across the list (AlignedTitles) */}
            <TitleWithChips title={title} chips={chips} />
            {/* Locked conditions grey themselves; the name and the rest stay as they are */}
            {summary && <Box minW={0}>{summary}</Box>}
            {description && (
              <Text fontSize="sm" color={MUTED}>
                {description}
              </Text>
            )}
          </VStack>

          {/* Editing hides the controls: Save or Cancel first */}
          {!editing && (
            // Edit, Delete and the lock sit just under the switch, right-aligned with it
            <VStack justify="start" align="end" gap={3} flexShrink={0}>
              {/* The status beside the switch it describes */}
              <HStack gap={3} h={6}>
                <StatusTag active={isEnabled} />
                {controls && (
                  <Switch
                    checked={isEnabled}
                    disabled={controls.toggling || controls.toggleDisabled}
                    onCheckedChange={(details) => controls.onToggle(details.checked)}
                    aria-label={`${label} active`}
                    size="sm"
                  />
                )}
              </HStack>
              {/* Edit, Delete, then the lock that governs them: switching on locks both */}
              {controls && (
                <HStack gap={4}>
                  {controls.onEdit && (
                    <IconAction
                      label={`Edit ${label}`}
                      icon={FaPenToSquare}
                      locked={isEnabled}
                      lockedReason="Uneditable whilst alert is active"
                      hoverColour="#39aed2"
                      onClick={controls.onEdit}
                    />
                  )}
                  {controls.onDelete && (
                    <IconAction
                      label={`Delete ${label}`}
                      icon={FaTrash}
                      locked={isEnabled}
                      lockedReason="Cannot be deleted whilst alert is active"
                      hoverColour="#9B2C2C"
                      onClick={controls.onDelete}
                    />
                  )}
                  <Box
                    color={isEnabled ? "active" : "primary"}
                    fontSize="sm"
                    aria-label={isEnabled ? "Locked whilst active" : "Unlocked"}
                    role="img"
                  >
                    {isEnabled ? <FaLock /> : <FaLockOpen />}
                  </Box>
                </HStack>
              )}
            </VStack>
          )}
        </HStack>

        {editing && children != null && (
          <Box mt={4} pt={4} borderTopWidth="1px" borderColor={EDITING_DIVIDER}>
            {children}
          </Box>
        )}
      </Card.Body>
    </Card.Root>
  )
}

function IconAction({
  label,
  icon,
  locked,
  lockedReason,
  hoverColour,
  onClick,
}: {
  label: string
  icon: typeof FaTrash
  locked: boolean
  lockedReason: string
  hoverColour: string
  onClick: () => void
}) {
  return (
    <Tooltip content={lockedReason} disabled={!locked}>
      {/* A span, so the tooltip still opens over a disabled button */}
      <Box as="span" display="inline-flex">
        <Box
          as="button"
          onClick={() => {
            if (!locked) onClick()
          }}
          aria-disabled={locked || undefined}
          aria-label={label}
          aria-description={locked ? lockedReason : undefined}
          display="inline-flex"
          color={locked ? "#C4C4C4" : MUTED}
          cursor={locked ? "not-allowed" : "pointer"}
          _hover={locked ? undefined : { color: hoverColour }}
        >
          <Icon as={icon} boxSize="15px" />
        </Box>
      </Box>
    </Tooltip>
  )
}

export default AlertSettingsCard
